import type { AddClientRequest, ClientContactPayload } from "../types/client.types";

export type ClientContactFormRow = {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  designation: string;
  isPrimary: boolean;
};

export type ClientFormFields = {
  name: string;
  type: string;
  sectorId: string | number | "";
  website: string;
  gstNumber: string;
  panNumber: string;
  billingAddress: string;
  shippingAddress: string;
  company: string;
  address: string;
  city: string;
  state: string;
  email: string;
  phone: string;
};

function contactRowMeaningful(row: ClientContactFormRow): boolean {
  return (
    String(row.firstName ?? "").trim() !== "" ||
    String(row.lastName ?? "").trim() !== "" ||
    String(row.email ?? "").trim() !== "" ||
    String(row.phone ?? "").trim() !== "" ||
    String(row.designation ?? "").trim() !== ""
  );
}

export function normalizeContactsForApi(
  rows: ClientContactFormRow[],
): ClientContactPayload[] {
  return rows
    .filter(contactRowMeaningful)
    .map((row) => ({
      ...(row.id != null && Number(row.id) > 0 ? { id: Number(row.id) } : {}),
      firstName: String(row.firstName ?? "").trim(),
      lastName: String(row.lastName ?? "").trim(),
      email: String(row.email ?? "").trim() || null,
      phone: String(row.phone ?? "").trim() || null,
      designation: String(row.designation ?? "").trim() || null,
      isPrimary: Boolean(row.isPrimary),
    }));
}

export function buildClientJsonPayload(
  fields: ClientFormFields,
  contacts: ClientContactFormRow[],
): AddClientRequest {
  const name = String(fields.name ?? "").trim();
  const companyTrim = String(fields.company ?? "").trim();
  const sectorRaw = fields.sectorId;
  const sectorId =
    sectorRaw !== "" && sectorRaw != null && Number.isFinite(Number(sectorRaw))
      ? Number(sectorRaw)
      : null;

  return {
    name,
    company: companyTrim || name,
    type: fields.type?.trim() || null,
    sectorId,
    website: String(fields.website ?? "").trim() || null,
    gstNumber: String(fields.gstNumber ?? "").trim() || null,
    panNumber: String(fields.panNumber ?? "").trim() || null,
    billingAddress: String(fields.billingAddress ?? "").trim() || null,
    shippingAddress: String(fields.shippingAddress ?? "").trim() || null,
    address: fields.address,
    city: fields.city,
    state: fields.state,
    email: fields.email,
    phone: fields.phone,
    contacts: normalizeContactsForApi(contacts),
  };
}

/** Multipart body for create/update when uploading `image`. */
export function clientFieldsToFormData(
  fields: ClientFormFields,
  contacts: ClientContactFormRow[],
  avatarFile: File | null,
): FormData {
  const fd = new FormData();
  (Object.entries(fields) as [keyof ClientFormFields, string | number][]).forEach(
    ([key, value]) => {
      if (value === "" || value == null) return;
      fd.append(key, String(value));
    },
  );
  const normalized = normalizeContactsForApi(contacts);
  fd.append("contacts", JSON.stringify(normalized));
  if (avatarFile instanceof File) {
    fd.append("image", avatarFile);
  }
  return fd;
}
